import React from 'react';
import moment from 'moment';
import {
  getCategoryName,
  getVisibleAttributes,
  parseColorValue, parseFontName, parseStrokeWidth
} from "../../utils/order/customAttributes";
import { OrderLineItem } from '../../queries/shopify/fragments/OrderLineItems';
import { VisibleAttributes } from "./sections";

export type CustomerEmailProps = {
  orderId: string;
  customerName: string;
  shipDate: Date;
  prevSentTime: number;
  items: OrderLineItem[];
  total: string;
  note: string;
};

const CustomerEmail = ({
  orderId,
  customerName,
  shipDate,
  prevSentTime,
  items,
  total,
  note,
}: CustomerEmailProps) => {
  return (
    <html lang="en" xmlns="http://www.w3.org/1999/xhtml">
      <head>
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>Order {orderId}</title>
      </head>
      <body
        style={{
          width: '100%',
          margin: 0,
          padding: 0,
          fontFamily: '"Arial", serif',
          WebkitTextSizeAdjust: '100%',
          MsTextSizeAdjust: '100%',
        }}
      >
        <p>Hi, {customerName || ''}!</p>

        {prevSentTime ? (
          <p>
            NEW estimated delivery date for order is{' '}
            <b>{moment(shipDate).format('dddd, MMMM Do, YYYY')}</b>.
          </p>
        ) : (
          <p>
            Your MK Socks order {orderId} has been passed to manufacturer.{' '}
            Estimated delivery date for order is{' '}
            <b>{moment(shipDate).format('dddd, MMMM Do, YYYY')}</b>.
          </p>
        )}

        <p>Here are the products you ordered:</p>

        {items.map((item, i) => {
          const productName = getCategoryName(item.customAttributes);
          const visibleAttrs = getVisibleAttributes(item.customAttributes);
          const itemPrice = getItemPrice(item);

          return (
            <div
              key={i}
              style={{
                paddingLeft: 48,
                paddingTop: 12,
                paddingRight: 24,
                paddingBottom: 12,
              }}
            >
              <p>
                <b>
                  {i + 1}. {item.title}
                </b>
              </p>

              <table style={{ width: '100%' }} border={0}>
                <tr>
                  <td>
                    <table
                      style={{ width: '20%', marginBottom: 20 }}
                      align="left"
                      border={0}
                    >
                      <tr>
                        <td>
                          <img
                            src={item.image.url}
                            alt={item.title}
                            width={250}
                            height={250}
                            style={{ objectFit: "contain", maxWidth: "100%" }}
                          />
                        </td>
                      </tr>
                    </table>

                    <table
                      style={{ width: '40%', marginBottom: 20 }}
                      align="left"
                      border={0}
                    >
                      <tr>
                        <td>
                          <div
                            style={{
                              marginTop: 0,
                              marginBottom: 10,
                              paddingLeft: 20,
                            }}
                          >
                            <b>{item.variantTitle}</b>
                          </div>

                          <div
                            style={{
                              marginTop: 0,
                              marginBottom: 10,
                              paddingLeft: 20,
                            }}
                          >
                            Selected options:
                          </div>

                          <VisibleAttributes visibleAttrs={visibleAttrs} />
                        </td>
                      </tr>
                    </table>

                    <table
                      style={{ width: '25%', marginBottom: 20 }}
                      align="left"
                      border={0}
                    >
                      <tr>
                        <td>
                          <div style={{ marginBottom: 10 }}>
                            <b>
                              {itemPrice} x {item.quantity}
                            </b>
                          </div>
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
              </table>
            </div>
          );
        })}

        <div>
          <table style={{ width: '70%' }} align="left" border={0}>
            <tr>
              <td>
                {note && note.length > 0 ? (
                  <div>
                    <h4 style={{ marginTop: 0 }}>Order note</h4>
                    <div>{note}</div>
                  </div>
                ) : null}
              </td>
            </tr>
          </table>

          <table style={{ width: '30%' }} align="right" border={0}>
            <tr>
              <td>
                <b>Total: {total}</b>
              </td>
            </tr>
          </table>
        </div>
      </body>
    </html>
  );
};

function getItemPrice(item: OrderLineItem) {
  if (!item.originalUnitPriceSet) return;
  const price = item.originalUnitPriceSet.shopMoney;
  return price.currencyCode + price.amount;
}

export default CustomerEmail;
